import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { fetchData } from '../../helpers/apiUtils';
import type { PlxBewerbbarkeitDetail, PlxMailZeitbezug } from '../../helpers/plxMailingData';

/**
 * Bewerbbarkeit einer PLX-Kampagne im Editor: Status, Gründe, KI-Zeitbezug je Mail mit Zitat,
 * Freigabe je Text und „Neu prüfen“. Regel und Begriffe: `.claude/docs/plx-promotion-gate.md`.
 * Die Freigabe gilt nur dem geprüften Text — jede Textänderung hebt sie auf.
 */
interface Props {
  campaignId: number;
  bewerbbarkeit: PlxBewerbbarkeitDetail | null;
  onChange: (b: PlxBewerbbarkeitDetail) => void;
}

const deDatum = (iso: string | null | undefined): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso ?? '');
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

function zeitbezugLabel(m: PlxMailZeitbezug): {
  text: string;
  color: 'default' | 'success' | 'warning' | 'error';
} {
  if (!m.zeitbindung) return { text: 'Noch nicht geprüft', color: 'default' };
  if (m.zeitbindung === 'keine') return { text: 'Zeitlos', color: 'success' };
  if (m.freigabe_at) return { text: 'Zeitbezug freigegeben', color: 'success' };
  if (m.zeitbindung === 'unklar') return { text: 'Zeitgebunden, Ablauf unklar', color: 'error' };
  return m.sperrt
    ? { text: `Abgelaufen seit ${deDatum(m.ablauf_datum)}`, color: 'error' }
    : { text: `Gültig bis ${deDatum(m.ablauf_datum)}`, color: 'warning' };
}

const PromotionStatusPanel: React.FC<Props> = ({ campaignId, bewerbbarkeit, onChange }) => {
  const [busy, setBusy] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);

  const neuPruefen = async () => {
    setBusy('rescreen');
    setFehler(null);
    const r = await fetchData<{
      bewerbbarkeit: PlxBewerbbarkeitDetail;
      pruefung: { ki_fehler: number; ki_aus: boolean };
    }>(`/api/plx-mailings/campaigns/${campaignId}/rescreen`, { method: 'POST' });
    setBusy(null);
    if (r.success && r.data) {
      onChange(r.data.bewerbbarkeit);
      if (r.data.pruefung.ki_aus)
        setFehler('KI-Prüfung ist nicht konfiguriert — Texte bleiben ungeprüft.');
      else if (r.data.pruefung.ki_fehler > 0)
        setFehler(`${r.data.pruefung.ki_fehler} Text(e) konnten nicht geprüft werden.`);
    } else {
      setFehler(r.error || 'Prüfung fehlgeschlagen');
    }
  };

  const freigabe = async (m: PlxMailZeitbezug, freigeben: boolean) => {
    setBusy(`f${m.email_id}`);
    setFehler(null);
    const r = await fetchData<{ bewerbbarkeit: PlxBewerbbarkeitDetail }>(
      `/api/plx-mailings/campaigns/${campaignId}/zeitbezug-freigabe`,
      { method: 'POST', body: JSON.stringify({ content_hash: m.content_hash, freigeben }) },
    );
    setBusy(null);
    if (r.success && r.data) onChange(r.data.bewerbbarkeit);
    else setFehler(r.error || 'Freigabe fehlgeschlagen');
  };

  if (!bewerbbarkeit) {
    return (
      <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#ffffff' }}>
        <Typography variant="subtitle2">Bewerbbarkeit</Typography>
        <Typography variant="body2" color="text.secondary">
          Noch kein Status — nach dem ersten Speichern verfügbar.
        </Typography>
      </Paper>
    );
  }

  const gesperrt = bewerbbarkeit.status === 'gesperrt';
  return (
    <Paper variant="outlined" sx={{ p: 2, backgroundColor: '#ffffff' }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="subtitle2">Bewerbbarkeit (Newsletter, Redlink)</Typography>
          <Chip
            size="small"
            label={gesperrt ? 'Gesperrt' : 'Bewerbbar'}
            color={gesperrt ? 'error' : 'success'}
          />
          {!bewerbbarkeit.gate_aktiv && (
            <Tooltip title="Notaus PLX_PROMO_GATE_ENABLED=0: Zeitbezug und Landing-Prüfung sperren gerade nicht.">
              <Chip size="small" label="Prüfung aus" variant="outlined" />
            </Tooltip>
          )}
        </Box>
        <Button size="small" variant="outlined" onClick={neuPruefen} disabled={busy !== null}>
          {busy === 'rescreen' ? <CircularProgress size={16} /> : 'Neu prüfen'}
        </Button>
      </Box>

      {bewerbbarkeit.hindernisse.map((h) => (
        <Alert key={h.code} severity="error" sx={{ mb: 0.5, py: 0 }}>
          {h.text}
        </Alert>
      ))}
      {bewerbbarkeit.warnungen.map((w) => (
        <Alert key={w.code} severity="warning" sx={{ mb: 0.5, py: 0 }}>
          {w.text}
        </Alert>
      ))}
      {fehler && (
        <Alert severity="error" sx={{ mb: 0.5, py: 0 }}>
          {fehler}
        </Alert>
      )}

      <Box mt={1} display="flex" flexDirection="column" gap={1}>
        {bewerbbarkeit.mails.map((m) => {
          const l = zeitbezugLabel(m);
          const zeitgebunden = m.zeitbindung === 'ereignis' || m.zeitbindung === 'unklar';
          return (
            <Box key={m.email_id} sx={{ borderTop: '1px solid #e0e0e0', pt: 1 }}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Mail {m.mailing_number}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 200 }}>
                  {m.subject}
                </Typography>
                <Chip size="small" label={l.text} color={l.color} variant="outlined" />
                {zeitgebunden && (
                  <Tooltip
                    title={
                      m.freigabe_at
                        ? `Freigegeben am ${deDatum(m.freigabe_at)}${m.freigabe_by ? ` von ${m.freigabe_by}` : ''}. Gilt nur für diesen Text.`
                        : 'Bestätigt, dass dieser Text trotz Zeitbezug weiter beworben werden darf. Gilt nur für genau diesen Text — jede Änderung hebt die Freigabe auf.'
                    }
                  >
                    <span>
                      <Button
                        size="small"
                        onClick={() => freigabe(m, !m.freigabe_at)}
                        disabled={busy !== null}
                      >
                        {busy === `f${m.email_id}` ? (
                          <CircularProgress size={14} />
                        ) : m.freigabe_at ? (
                          'Freigabe zurücknehmen'
                        ) : (
                          'Zeitbezug geprüft, freigeben'
                        )}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Box>
              {zeitgebunden &&
                m.gruende.slice(0, 3).map((g, i) => (
                  <Typography
                    key={i}
                    variant="caption"
                    display="block"
                    color="text.secondary"
                    sx={{ pl: 1 }}
                  >
                    {g.zitat ? `„${g.zitat}“ — ` : ''}
                    {g.warum}
                  </Typography>
                ))}
            </Box>
          );
        })}
        {bewerbbarkeit.mails.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Keine Mail mit Inhalt.
          </Typography>
        )}
      </Box>

      {bewerbbarkeit.landing && (
        <Typography variant="caption" display="block" color="text.secondary" mt={1}>
          Landing-Seite:{' '}
          {bewerbbarkeit.landing.ok === null
            ? 'noch nicht eindeutig geprüft'
            : bewerbbarkeit.landing.ok
              ? `erreichbar (HTTP ${bewerbbarkeit.landing.http_status}, ${deDatum(bewerbbarkeit.landing.checked_at)})`
              : `nicht erreichbar (HTTP ${bewerbbarkeit.landing.http_status}, ${deDatum(bewerbbarkeit.landing.checked_at)})`}
        </Typography>
      )}
    </Paper>
  );
};

export default PromotionStatusPanel;
